import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, interval, map, of, switchMap, takeUntil, tap } from "rxjs";
import { AuthService } from "../../services/auth.service";
import { UserActions } from "../actions";
import { CookieService } from "ngx-cookie-service";
import { Router } from "@angular/router";
import { UserFacade } from "../facade/user.facade";
import { environment } from "../../../environments/environment";
import { ERROR } from "../../variables/uri";

@Injectable({
    providedIn: "root",
})
export class UserEffects {
    private actions$ = inject(Actions);
    private cookiesService = inject(CookieService);
    private router = inject(Router);
    private userFacade = inject(UserFacade);
    private lastToken: string | null = null;
    private authService = inject(AuthService);

    loadUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.loadUser),
            switchMap(() =>
                this.authService.getUserInfo().pipe(
                    map((user) => UserActions.loadUserSuccess({ user })),
                    catchError((error) =>
                        of(UserActions.loadUserFailure(error)),
                    ),
                ),
            ),
        );
    });

    monitorSession$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.startSessionMonitoring),
            switchMap(() =>
                interval(5000).pipe(
                    map(() => {
                        const currentToken = this.cookiesService.get(
                            environment.cookieName,
                        );

                        if (!currentToken) {
                            console.warn("Session expired.");
                            this.authService.logout();
                            return UserActions.sessionExpired();
                        }

                        if (this.lastToken && this.lastToken !== currentToken) {
                            console.warn("Token has changed - logging out.");
                            this.authService.logout();
                            return UserActions.sessionTokenChanged();
                        }

                        this.lastToken = currentToken;
                        return { type: "NO_ACTION" };
                    }),
                    takeUntil(
                        this.actions$.pipe(ofType(UserActions.sessionExpired)),
                    ),
                ),
            ),
        );
    });

    sessionExpired$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.sessionExpired),
            tap(() => {
                this.userFacade.clearUser();
            }),
            map(() => UserActions.goToNotAuthenticatedPage()),
        );
    });

    sessionTokenChanged$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.sessionTokenChanged),
            tap(() => {
                this.userFacade.clearUser();
            }),
            map(() => UserActions.goToNotAuthenticatedPage()),
        );
    });

    redirectToLogin$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(UserActions.redirectToLogin),
                tap(() => {
                    this.authService.redirectToLogin();
                }),
            );
        },
        { dispatch: false },
    );

    goToNotAuthenticatedPage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(UserActions.goToNotAuthenticatedPage),
                tap(() => {
                    this.router.navigate([ERROR.NOT_AUTHENTICATED]);
                }),
            );
        },
        { dispatch: false },
    );
}
